import { Box, Button, Grid, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useContext, useEffect } from "react";
import { COMMISSIONING_PHASE_KEYS } from "../enum/commissioning_phase_keys";
import { DeviceCommissioningOverview } from "../phase_detail_components/device_overview/device_commissioning_overview";
import { VerifyDeviceLiveData } from "../phase_detail_components/verify_device/verify_device_live_data";
import { CommissioningStateContext } from "../hooks/use_commissioning_state_changes";
import Loader from "../../../../../../components/general/loader";
import FinalVerification from "../phase_detail_components/final_verification/final_verification";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { hasPermission, isComponentDisabledForUpdates } from "../../../../../login/login_utils";
import Privilege from "../../../../../../enums/privilege_enum";
import { COMMISSIONING_PROCEDURE_ACTIONS } from "../enum/commissioning_procedure_actions";
import { useUserPrivilegeKeyPermissionsMap } from "../../../../../../hooks/use_backend";
import { isCompleted } from "../enum/commissioning_required_action";

export default function PhaseDetailsPanel({ required_privilege }) {
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const {
    commissioningState,
    isLoadingCommissioning,
    commissioningError,
    isStartResetError,
    isStartResetMutating,
    isHardResetMutating,
    startResetMutation,
    hardResetMutation,
  } = useContext(CommissioningStateContext);
  const verificationIsCommissioned = commissioningState.finalVerificationStep
    ? isCompleted(commissioningState.finalVerificationStep.required_action)
    : false;

  useEffect(() => {}, [commissioningState.selectedPhaseKey]);

  function getDetailedPhaseComponent(phase_key) {
    if (phase_key === COMMISSIONING_PHASE_KEYS.DEVICE_OVERVIEW) {
      return <DeviceCommissioningOverview />;
    } else if (
      commissioningState.selectedDevice &&
      [
        COMMISSIONING_PHASE_KEYS.GRID_METER,
        COMMISSIONING_PHASE_KEYS.METERS,
        COMMISSIONING_PHASE_KEYS.BATTERIES,
        COMMISSIONING_PHASE_KEYS.SOLAR_INVERTERS,
      ].includes(phase_key)
    ) {
      return <VerifyDeviceLiveData requiredPrivilege={required_privilege} />;
    } else if (commissioningState.finalVerificationStep && phase_key === COMMISSIONING_PHASE_KEYS.FINAL_VERIFICATION) {
      return <FinalVerification requiredPrivilege={required_privilege} />;
    }
  }

  function resetButtons() {
    const title = "Reset the ongoing procedure's progress";
    const buttonText = "Reset";
    const disabledForUpdates = isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());
    const isAdmin = hasPermission(permissionMap, Privilege.ADMIN_OVERVIEW.getPrivilegeKey(), false);
    return (
      <>
        <Box display="flex" alignItems="stretch" gap={1}>
          {isAdmin && (
            <Tooltip
              title={
                "Hard reset which will reset to the initial commissioning procedure state - ALL COMMISSIONING COMPLETION WILL BE LOST"
              }
            >
              <Button
                disabled={!isAdmin}
                variant="text"
                color="inherit"
                size="medium"
                sx={{ borderRadius: "2", lineHeight: 1, flex: 1, cursor: "pointer", fontSize: "0.7rem" }}
                startIcon={<RotateLeftIcon sx={{ color: "error.main" }} />}
                onClick={() => {
                  const confirmed = window.confirm(
                    "Are you sure you want to continue? - ALL COMMISSIONING COMPLETION WILL BE LOST",
                  );
                  if (confirmed) {
                    hardResetMutation({});
                  }
                }}
              >
                {"Factory reset"}
              </Button>
            </Tooltip>
          )}
          <Tooltip title={title}>
            <Button
              disabled={
                verificationIsCommissioned ||
                !commissioningState.commissioningData.has_devices_in_commissioning ||
                disabledForUpdates ||
                (commissioningState.validationState.start_reset_disabled &&
                  !(isAdmin && commissioningState.validationState.has_uncommissioned_devices))
              }
              variant="text"
              color="inherit"
              size="medium"
              sx={{ borderRadius: "2", lineHeight: 1, flex: 1, cursor: "pointer", fontSize: "0.7rem" }}
              startIcon={<RotateLeftIcon sx={{ color: "warning.main" }} />}
              onClick={() => {
                if (commissioningState.validationState.commissioning_ongoing) {
                  const confirmed = window.confirm(
                    "Are you really sure you want to completely reset the current commissioning procedure? You will have to go through all steps again for all uncomissioned devices",
                  );
                  if (confirmed) {
                    startResetMutation({ action_type: COMMISSIONING_PROCEDURE_ACTIONS.RESET });
                  }
                }
              }}
            >
              {buttonText}
            </Button>
          </Tooltip>
        </Box>
      </>
    );
  }

  return commissioningState.commissioningData &&
    commissioningState.selectedPhaseKey &&
    commissioningState.selectedPhase ? (
    <>
      <Grid item xs={12} md={9}>
        <Paper sx={{ minHeight: "50vh" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              textAlign: "left",
              padding: "10px",
            }}
          >
            <Typography
              fontWeight="700"
              fontSize={30}
              noWrap
              sx={{
                textTransform: "capitalize",
                marginLeft: 1,
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {commissioningState.selectedDevice
                ? `${commissioningState.selectedPhase.phase_title}: ${commissioningState.selectedDevice.device_name}`
                : commissioningState.selectedPhase.phase_title}
            </Typography>
            {resetButtons()}
          </Box>

          <div style={{ padding: "10px" }}>{getDetailedPhaseComponent(commissioningState.selectedPhase.phase_key)}</div>
        </Paper>
      </Grid>
    </>
  ) : isLoadingCommissioning || isStartResetMutating || isHardResetMutating ? (
    <Grid item xs={12} md={9}>
      <Paper sx={{ boxShadow: 1, flexGrow: 1, minHeight: "50vh" }}>
        <Loader fill={true} sx={{ padding: "5px" }} />
      </Paper>
    </Grid>
  ) : commissioningError || isStartResetError ? (
    <Grid item xs={12} md={9}>
      <Paper sx={{ boxShadow: 1, minHeight: "50vh" }}>
        <div>Error loading data</div>
      </Paper>
    </Grid>
  ) : null;
}
